import { ArrowPathIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAnalytics } from "../../lib/analytics";
import { api } from "../../lib/api";
import { useClientTranslations } from "../../lib/i18n/client";
import { BottomSheet } from "../ui/BottomSheet";
import { ANALYTICS_EVENTS } from "./constants";
import { isRTLText } from "./helpers";
import { BookmarkFilledIcon, BookmarkOutlineIcon } from "./icons";
import styles from "./SuggestedQuestionsOverlay.module.css";

interface SuggestedQuestion {
	id: string;
	text: string;
	categoryName: string;
	categorySlug: string;
	isBookmarked?: boolean;
}

interface SuggestedQuestionsOverlayProps {
	isOpen: boolean;
	onClose: () => void;
	onQuestionSelect: (
		question: string,
		metadata?: { categoryName: string; categorySlug: string },
	) => void;
	chatId?: string;
}

export function SuggestedQuestionsOverlay({
	isOpen,
	onClose,
	onQuestionSelect,
	chatId,
}: SuggestedQuestionsOverlayProps) {
	const { t } = useClientTranslations("common");
	const { t: tChat } = useClientTranslations("chat");
	const { logEvent } = useAnalytics();

	const [questions, setQuestions] = useState<SuggestedQuestion[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [isFetching, setIsFetching] = useState(false);
	const [bookmarkingStates, setBookmarkingStates] = useState<
		Record<string, boolean>
	>({});
	const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(
		null,
	);

	const fetchingRef = useRef(false);
	const selectionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const fetchQuestions = useCallback(
		async (refresh = false) => {
			if (fetchingRef.current) return;

			fetchingRef.current = true;
			setIsFetching(true);
			setError(null);

			if (refresh) {
				setQuestions([]);
				setIsInitialLoad(true);
			}

			try {
				const baseUrl = "/api/ama/questions";
				const params = new URLSearchParams();
				if (refresh) params.set("refresh", "true");
				if (chatId) params.set("chatId", chatId);

				const url = params.toString()
					? `${baseUrl}?${params.toString()}`
					: baseUrl;

				const response = await api.get(url);
				const questionsArray = Array.isArray(response.data)
					? response.data
					: response.data.questions || [];

				if (Array.isArray(questionsArray) && questionsArray.length > 0) {
					setQuestions(questionsArray.slice(0, 4));
				} else {
					throw new Error("No questions received");
				}
			} catch (err) {
				console.error("Error fetching questions:", err);
				setError(tChat("suggestedQuestions.error"));
				setQuestions([]);
			} finally {
				setIsInitialLoad(false);
				setIsFetching(false);
				fetchingRef.current = false;
			}
		},
		[chatId, tChat],
	);

	// Clear stale questions when chatId changes (new chat session)
	const prevChatIdRef = useRef(chatId);
	useEffect(() => {
		if (chatId !== prevChatIdRef.current) {
			prevChatIdRef.current = chatId;
			setQuestions([]);
		}
	}, [chatId]);

	// Fetch questions when overlay opens for the first time only
	// Re-opening preserves existing questions (including bookmark state)
	useEffect(() => {
		if (isOpen) {
			logEvent("chat_suggested_questions_shown");
			if (questions.length === 0 && !fetchingRef.current) {
				fetchQuestions();
			}
		}
		if (!isOpen) {
			setSelectedQuestionId(null);
			if (selectionTimerRef.current) {
				clearTimeout(selectionTimerRef.current);
				selectionTimerRef.current = null;
			}
		}
		return () => {
			if (selectionTimerRef.current) {
				clearTimeout(selectionTimerRef.current);
				selectionTimerRef.current = null;
			}
		};
	}, [isOpen, questions.length, fetchQuestions, logEvent]);

	const handleQuestionClick = (question: SuggestedQuestion) => {
		if (selectedQuestionId) return;

		setSelectedQuestionId(question.id);
		logEvent("chat_suggested_question_selected", undefined, {
			question_id: question.id,
			question_text: question.text,
			category_name: question.categoryName,
		});

		selectionTimerRef.current = setTimeout(() => {
			selectionTimerRef.current = null;
			onQuestionSelect(question.text, {
				categoryName: question.categoryName,
				categorySlug: question.categorySlug,
			});
			onClose();
		}, 1000);
	};

	const toggleBookmark = async (
		question: SuggestedQuestion,
		event: React.MouseEvent,
	) => {
		event.stopPropagation();

		const questionId = question.id;
		const currentlyBookmarked = !!question.isBookmarked;
		const action = currentlyBookmarked ? "remove" : "add";

		// Optimistic UI update
		setBookmarkingStates((prev) => ({ ...prev, [questionId]: true }));
		setQuestions((prev) =>
			prev.map((q) =>
				q.id === questionId ? { ...q, isBookmarked: !currentlyBookmarked } : q,
			),
		);

		try {
			const response = await api.post("/api/ama/questions/bookmark", {
				questionText: question.text,
				categoryName: question.categoryName,
				categorySlug: question.categorySlug,
				action,
			});

			if (!response.data.success) {
				throw new Error("Bookmark action was not successful");
			}

			logEvent(ANALYTICS_EVENTS.CHAT_QUESTION_BOOKMARKED, undefined, {
				question_id: questionId,
				action: action,
				category_name: question.categoryName,
			});
			toast.success(
				action === "add"
					? tChat("toast.bookmarkAdded")
					: tChat("toast.bookmarkRemoved"),
			);
		} catch (err) {
			console.error("Error toggling bookmark:", err);
			// Revert optimistic update
			setQuestions((prev) =>
				prev.map((q) =>
					q.id === questionId ? { ...q, isBookmarked: currentlyBookmarked } : q,
				),
			);
			toast.error(tChat("toast.failedToUpdateBookmark"));
		} finally {
			setBookmarkingStates((prev) => ({ ...prev, [questionId]: false }));
		}
	};

	const handleRegenerate = () => {
		logEvent("chat_suggested_refresh_clicked");
		fetchQuestions(true);
	};

	const renderSkeletonCards = () =>
		[1, 2, 3, 4].map((index) => (
			<div key={`skeleton-${index}`} className={styles.questionRow}>
				<div className={styles.bookmarkButton} />
				<div className={styles.questionCard}>
					<div className={styles.iconContainer}>
						<img
							src="/images/sparkling-icon.png"
							alt="AI"
							className={`${styles.amaIcon} ${styles.iconFaded}`}
						/>
					</div>
					<div className={styles.skeletonText} />
				</div>
			</div>
		));

	const renderQuestionCards = () =>
		questions.map((question) => {
			const isRTL = isRTLText(question.text);
			const isSelected = selectedQuestionId === question.id;
			const isDisabled = selectedQuestionId !== null && !isSelected;

			return (
				<div key={question.id} className={styles.questionRow}>
					<button
						type="button"
						onClick={(e) => toggleBookmark(question, e)}
						disabled={bookmarkingStates[question.id]}
						aria-label={
							question.isBookmarked ? "Remove bookmark" : "Add bookmark"
						}
						aria-pressed={!!question.isBookmarked}
						title={question.isBookmarked ? "Bookmarked" : "Bookmark"}
						className={styles.bookmarkButton}
					>
						{question.isBookmarked ? (
							<BookmarkFilledIcon />
						) : (
							<BookmarkOutlineIcon />
						)}
					</button>

					<button
						type="button"
						onClick={() => handleQuestionClick(question)}
						disabled={isDisabled}
						className={`${styles.questionCard} ${isSelected ? styles.questionCardSelected : ""}`}
					>
						<div className={styles.iconContainer}>
							<img
								src="/images/sparkling-icon.png"
								alt="AI"
								className={`${styles.amaIcon} ${isSelected ? styles.iconInverted : ""}`}
							/>
						</div>
						<div className={styles.questionTextWrapper}>
							<p
								className={`${styles.questionText} ${isSelected ? styles.questionTextSelected : ""} ${isRTL ? styles.textRTL : ""}`}
								dir={isRTL ? "rtl" : "ltr"}
							>
								{question.text}
							</p>
						</div>
					</button>
				</div>
			);
		});

	const renderContent = () => {
		if (!isInitialLoad && questions.length === 0 && error) {
			return (
				<div className={styles.errorContainer}>
					<p className={styles.errorText}>{error}</p>
					<button
						type="button"
						onClick={() => fetchQuestions(true)}
						className={styles.retryButton}
					>
						{t("buttons.tryAgain")}
					</button>
				</div>
			);
		}

		return (
			<div
				className={styles.questionsList}
				aria-busy={isInitialLoad || isFetching}
			>
				{isInitialLoad || (isFetching && questions.length === 0)
					? renderSkeletonCards()
					: renderQuestionCards()}
			</div>
		);
	};

	const headerContent = (
		<div className={styles.headerRow}>
			<img
				src="/images/sparkling-icon.png"
				alt=""
				className={styles.headerIcon}
			/>
			<h2 id="overlay-title" className={styles.headerTitle}>
				{tChat("suggestedQuestions.title")}
			</h2>
			<button
				type="button"
				className={styles.closeButton}
				onClick={onClose}
				aria-label={t("buttons.close")}
			>
				<XMarkIcon className={styles.closeIcon} />
			</button>
		</div>
	);

	const footerContent = (
		<button
			type="button"
			onClick={handleRegenerate}
			disabled={isFetching}
			className={styles.regenerateButton}
		>
			<ArrowPathIcon className={styles.regenerateIcon} />
			{tChat("suggestedQuestions.regenerate")}
		</button>
	);

	return (
		<BottomSheet
			isOpen={isOpen}
			onClose={onClose}
			ariaLabelledBy="overlay-title"
			header={headerContent}
			footer={footerContent}
		>
			{renderContent()}
		</BottomSheet>
	);
}
